import React, { useContext, useState, useEffect } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import { Input } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { Error } from '@progress/kendo-react-labels';
import { orderBy, filterBy } from '@progress/kendo-data-query';
import Loader from '../../components/Loader';
import UpdateController from '../../components/UpdateController';
import { useLabelsHook } from '../../hooks';
import { LocalizationContext } from '../../contexts';
import { labelColumns } from '../../config';
import './index.scss';

// All column IDs for the labels grid (he, ar, en, ivritil)
const allLabelColumnIds = labelColumns.map(col => col.id);

export default function LabelsManagement(props) {
    const [localization] = useContext(LocalizationContext);
    const { isLabelsLoading, errorLabelsLoading, clearErrorLabelsLoading, labels, lastEditData, updateLabels } = useLabelsHook();

    const [items, setItems] = useState([]);
    const [newLabelError, setnewLabelError] = useState(false);
    const [newLabel, setnewLabel] = useState(false);
    const [updateData, setUpdateData] = useState(false);

    const [gridSort, setGridSort] = useState({ allowUnsort: true, multiple: false, sort: [] });
    const [gridFilter, setGridFilter] = useState({ logic: "and", filters: [] });
    const [gridItems, setGridItems] = useState([]);

    useEffect(() => {
        setItems([...labels]);
        updateGridItems([...labels], gridSort.sort, gridFilter);
    }, [labels]);

    const updateGridItems = (items, sort, filter, labelEditKey) => {
        var sortItems = orderBy(items, sort);
        var filterItems = filterBy(sortItems, filter);
        filterItems.forEach(label => {
            label.inEdit = label.key == labelEditKey;
        });
        setGridItems(filterItems);
    };

    const sortChange = (event) => {
        setGridSort({ ...gridSort, sort: event.sort });
        updateGridItems([...items], event.sort, gridFilter);
    }

    const filterChange = (event) => {
        setGridFilter(event.filter);
        updateGridItems([...items], gridSort.sort, event.filter);
    }

    const rowClick = (event) => {
        updateGridItems([...items], gridSort.sort, gridFilter, event.dataItem.key);
    };

    const itemChange = (event) => {
        var newItems = [...items];
        newItems.filter(label => label.key == event.dataItem.key).forEach(label => { label[event.field] = event.value; });
        setItems(newItems);
        updateGridItems([...newItems], gridSort.sort, gridFilter, event.dataItem.key);

        var changes = updateData ? { ...updateData } : {};
        if (!(event.field in changes)) {
            changes[event.field] = {};
        }
        changes[event.field][event.dataItem.key] = event.value;
        setUpdateData(changes);
    };

    //#region  createnewLabel
    const createnewLabel = () => {
        var newLabel = { key: '' };
        // Initialize all label columns (he, ar, en, ivritil)
        for (var i = 0; i < allLabelColumnIds.length; i++) {
            newLabel[allLabelColumnIds[i]] = '';
        }
        setnewLabelError(localization.labels.labelsManagement.new_label_error_empty_key);
        setnewLabel(newLabel);
        updateGridItems([...items], gridSort.sort, gridFilter);
    };

    const cancelAddnewLabel = () => {
        setnewLabel(false);
    };

    const addnewLabel = () => {
        if (items.filter(item => item.key == newLabel.key).length > 0) {
            setnewLabelError(localization.labels.labelsManagement.new_label_error_existing_key);
        }
        else {
            var changes = updateData ? { ...updateData } : {};
            // Include all label columns (he, ar, en, ivritil)
            allLabelColumnIds.forEach(columnId => {
                if (!(columnId in changes)) {
                    changes[columnId] = {};
                }
                changes[columnId][newLabel.key] = newLabel[columnId];
            });
            setUpdateData(changes);

            var newItems = [newLabel, ...items];
            setItems(newItems);
            updateGridItems([...newItems], gridSort.sort, gridFilter);
            cancelAddnewLabel();
        }
    };
    //#endregion


    const update = () => {
        var fetch = updateLabels({ ...updateData });
        fetch.then(() => {
            setUpdateData(false);
        });
        return fetch;
    };

    const newLabelPopup = <Dialog className="app-labels_management-new_label_popup">
        <div>
            <Input className={"labels_management-new_label_popup-entry--key"} label={localization.labels.key} value={newLabel.key} onChange={(event) => {
                var _newLabel = { ...newLabel };
                var newVal = event.target.value.trim();
                _newLabel.key = newVal;
                setnewLabel(_newLabel);
                if (newVal == '') {
                    setnewLabelError(localization.labels.labelsManagement.new_label_error_empty_key);
                }
                else if (newVal.length > 200) {
                    setnewLabelError(localization.labels.labelsManagement.new_label_error_max_key_size);
                }
                else {
                    setnewLabelError(false);
                }
            }} />
            {newLabelError && <Error>{newLabelError}</Error>}
        </div>
        {allLabelColumnIds.map(columnId => {
            return (
                <div key={"new_label_input_" + columnId}>
                    <Input className={"labels_management-new_label_popup-entry--" + columnId} value={newLabel[columnId]} label={columnId in localization.labels ? localization.labels[columnId] : columnId} onChange={(event) => {
                        var _newLabel = { ...newLabel };
                        _newLabel[columnId] = event.target.value;
                        setnewLabel(_newLabel);
                    }} />
                </div>
            );
        })}
        <DialogActionsBar>
            <Button primary={!newLabelError} disabled={!!newLabelError} onClick={addnewLabel}>{localization.labels.labelsManagement.add_new_label}</Button>
            <Button onClick={cancelAddnewLabel}>{localization.labels.labelsManagement.cancel}</Button>
        </DialogActionsBar>
    </Dialog>;

    return (
        <div className="app-labels_management">
            <UpdateController lastEditor={lastEditData.lastEditor} lastUpdate={lastEditData.lastUpdate} hasChanges={!!updateData} update={update}>
                {(isLabelsLoading) && <Loader />}
                {(errorLabelsLoading) && <Dialog onClose={() => { clearErrorLabelsLoading(); }} title={localization.labels.loading_error_title}><p>{localization.labels.loading_error_message}</p></Dialog>}
                {newLabel && newLabelPopup}
                <Grid
                    className="app-grid"
                    data={gridItems}
                    total={gridItems.length}
                    sortable={{
                        allowUnsort: gridSort.allowUnsort,
                        mode: gridSort.multiple ? 'multiple' : 'single'
                    }}
                    sort={gridSort.sort}
                    onSortChange={sortChange}
                    filterable
                    filter={gridFilter}
                    onFilterChange={filterChange}
                    editField="inEdit"
                    onRowClick={rowClick}
                    onItemChange={itemChange}
                >
                    <GridToolbar>
                        <div className="app-labels_management-grid-tools">
                            <div className="app-labels_management-grid-tools--left"></div>
                            <div className="app-labels_management-grid-tools--right">
                                <Button className="new_label k-button" primary={true} onClick={createnewLabel}>{localization.labels.labelsManagement.new_label}</Button>
                            </div>
                        </div>
                    </GridToolbar>
                    {
                        ["key", ...allLabelColumnIds].map((columnId, i) => {
                            return <Column className={"labels_management-column--" + columnId} field={columnId} title={columnId in localization.labels ? localization.labels[columnId] : columnId} filter="text" editable={columnId != 'key'} key={"column_" + i} />
                        })
                    }
                </Grid>
            </UpdateController>
        </div>
    );
};